/**
 * Changes through the guide (issue #9): fill or clear a Choice, answer a prompt, advance a Level.
 * After every change the Character is settled (grants reconciled, sheet derived) and every other
 * Selection and Answer is checked for structural legality; those no longer legal are reopened
 * (cleared) and reported, so Retraining an early Choice reopens only what depended on it. The
 * caller previews the reopen set before committing.
 */
import type { AnswerValue, Character, LevelNumber } from "../character/model";
import { choiceKey, findChoice, isGranted } from "../character/model";
import { clearSelection, setAnswer, setSelection } from "../character/grants";
import { ensureLevels } from "../character/create";
import { apexCandidates, boostCandidates, keyAbilityCandidates, selectionRuling, skillIncreaseCandidates, skillPotencyCandidates, slotBoostCandidates } from "./candidates";
import type { GuideContext } from "./context";
import { levelOf } from "./context";
import { expectedSlots, intelligenceRises } from "./slots";
import { guideLevels } from "./steps";

export type GuideChange =
  | { kind: "select"; key: string; uuid: string }
  | { kind: "clear"; key: string }
  | { kind: "answer"; key: string; flag: string; value: AnswerValue | null };

export interface Reopened {
  key: string;
  level: LevelNumber;
  /** The flag of a reopened Answer; null for a cleared Selection. */
  flag: string | null;
  label: string;
  reason: string;
}

export interface ChangeOutcome {
  ctx: GuideContext;
  /** Set when nothing changed: a grant with `restrict` forbids it. */
  blocked: string | null;
  reopened: Reopened[];
}

/** Settle a Character: reconcile grants, load what the engine needs, derive; the app's version loads Entries from the Snapshot. */
export type Settle = (character: Character) => Promise<GuideContext>;

/** Apply one change without settling (pure). */
export function applyChangeOnce(ctx: GuideContext, change: GuideChange): { character: Character; blocked: string | null } {
  const character = ctx.character;
  switch (change.kind) {
    case "select": {
      const existing = findChoice(character, change.key);
      if (!existing) return { character, blocked: `no Choice ${change.key}` };
      if (existing.selection?.uuid === change.uuid) return { character, blocked: null };
      return setSelection(character, change.key, change.uuid, {}, ctx.sheet);
    }
    case "clear":
      return clearSelection(character, change.key, ctx.sheet);
    case "answer": {
      const existing = findChoice(character, change.key);
      if (!existing) return { character, blocked: `no Choice ${change.key}` };
      // A skill training answered at Level 1 may sit beyond the count; keep the answers dense by clearing.
      return setAnswer(character, change.key, change.flag, change.value);
    }
  }
}

const MAX_REOPEN_PASSES = 6;

/**
 * Apply a change, settle, then reopen whatever the change made illegal until nothing else
 * changes. The returned context is settled and its Character holds the reopened Choices.
 */
export async function applyChange(ctx: GuideContext, change: GuideChange, settle: Settle): Promise<ChangeOutcome> {
  const first = applyChangeOnce(ctx, change);
  if (first.blocked) return { ctx, blocked: first.blocked, reopened: [] };
  const changedKey = change.key;
  const changedFlag = change.kind === "answer" ? change.flag : null;
  const settled = await reopenIllegal(await settle(first.character), settle, (r) => !(r.key === changedKey && r.flag === changedFlag));
  return { ctx: settled.ctx, blocked: null, reopened: settled.reopened };
}

/**
 * Reopen whatever is not structurally legal in a settled context until nothing else changes: the
 * cleared Selections and Answers are reported. `keep` exempts what the caller just changed.
 */
export async function reopenIllegal(start: GuideContext, settle: Settle, keep: (r: Reopened) => boolean = () => true): Promise<{ ctx: GuideContext; reopened: Reopened[] }> {
  let current = start;
  const reopened: Reopened[] = [];
  for (let pass = 0; pass < MAX_REOPEN_PASSES; pass++) {
    const illegal = illegalChoices(current).filter(keep);
    if (illegal.length === 0) break;
    let character = current.character;
    for (const r of illegal) {
      const cleared = r.flag === null ? clearSelection(character, r.key, current.sheet) : setAnswer(character, r.key, r.flag, null);
      if (cleared.blocked) continue;
      character = cleared.character;
      reopened.push(r);
    }
    if (character === current.character) break;
    current = await settle(character);
  }
  return { ctx: current, reopened };
}

/** Every Selection or Answer on the Character that is not structurally legal now. */
export function illegalChoices(ctx: GuideContext): Reopened[] {
  const out: Reopened[] = [];
  const character = ctx.character;
  const levels = guideLevels(ctx);
  const labelOf = (key: string, flag: string | null): string => {
    for (const l of levels) for (const i of l.items) if (i.choiceKey === key && ("flag" in i ? i.flag : null) === flag) return i.label;
    return key;
  };
  for (const level of character.levels) {
    if (level.number > character.level) continue;
    for (const choice of level.choices) {
      if (isGranted(choice.source) && !choice.detached) continue;
      const key = choiceKey(level.number, choice.source);
      const ruling = selectionRuling(ctx, level.number, choice);
      if (ruling && !ruling.legal) {
        out.push({ key, level: level.number, flag: null, label: labelOf(key, null), reason: ruling.reason ?? "no longer legal" });
        continue;
      }
      const answers = choice.answers ?? {};
      if (choice.source.kind === "progression" && choice.source.slot === "attribute-boosts" && typeof answers.boost === "string") {
        const candidate = boostCandidates(ctx, key).find((c) => c.attribute === answers.boost);
        if (candidate && !candidate.legal) out.push({ key, level: level.number, flag: "boost", label: labelOf(key, "boost"), reason: candidate.reason ?? "not legal" });
      }
      if (choice.source.kind === "progression" && choice.source.slot === "skill-potency" && typeof answers.skill === "string") {
        const candidate = skillPotencyCandidates(ctx, key).find((c) => c.slug === answers.skill);
        if (!candidate) out.push({ key, level: level.number, flag: "skill", label: labelOf(key, "skill"), reason: "not a skill on the sheet" });
        else if (!candidate.legal) out.push({ key, level: level.number, flag: "skill", label: labelOf(key, "skill"), reason: candidate.reason ?? "not legal" });
      }
      if (choice.source.kind === "progression" && choice.source.slot === "attribute-apex" && typeof answers.attribute === "string") {
        if (!apexCandidates(ctx, key).some((c) => c.attribute === answers.attribute && c.legal)) out.push({ key, level: level.number, flag: "attribute", label: labelOf(key, "attribute"), reason: "not an attribute" });
      }
      if (choice.source.kind === "progression" && choice.source.slot === "skill-increase" && typeof answers.skill === "string") {
        const intelligence = expectedSlots(level.number, ctx.tables, character.variantRules, intelligenceRises(ctx.track, level.number)).some((s) => s.slot === "skill-increase" && s.ordinal === (choice.source as { ordinal: number }).ordinal && s.intelligence);
        const candidate = skillIncreaseCandidates(ctx, key, intelligence).find((c) => c.slug === answers.skill);
        if (candidate && !candidate.legal) out.push({ key, level: level.number, flag: "skill", label: labelOf(key, "skill"), reason: candidate.reason ?? "not legal" });
      }
      if ((choice.source.kind === "ancestry" || choice.source.kind === "background") && choice.selection) {
        for (const [flag, value] of Object.entries(answers)) {
          if (!/^(boost|flaw|alternate):\d+$/.test(flag) || typeof value !== "string") continue;
          const candidate = slotBoostCandidates(ctx, choice.source.kind, flag).find((c) => c.attribute === value);
          if (!candidate) out.push({ key, level: 1, flag, label: labelOf(key, flag), reason: "not one of this Entry's options" });
          else if (!candidate.legal) out.push({ key, level: 1, flag, label: labelOf(key, flag), reason: candidate.reason ?? "not legal" });
        }
      }
      if (choice.source.kind === "class" && choice.selection && typeof answers.keyAbility === "string") {
        const options = keyAbilityCandidates(ctx);
        if (options.length > 0 && !options.some((c) => c.attribute === answers.keyAbility)) out.push({ key, level: 1, flag: "keyAbility", label: labelOf(key, "keyAbility"), reason: "not a key attribute of this class" });
      }
    }
  }
  // ChoiceSet answers the engine judged illegal (the candidate list no longer holds them).
  for (const prompt of ctx.sheet.prompts) {
    if (prompt.legal !== false || !prompt.choiceKey) continue;
    const choice = findChoice(character, prompt.choiceKey);
    if (!choice || (isGranted(choice.source) && !choice.detached && !(prompt.flag in (choice.answers ?? {})))) continue;
    if (!(prompt.flag in (choice.answers ?? {}))) continue;
    out.push({ key: prompt.choiceKey, level: levelOf(prompt.choiceKey), flag: prompt.flag, label: `${prompt.name}: ${prompt.prompt}`, reason: prompt.note ?? "the answer is not among the candidates" });
  }
  return out;
}

/** Advance the Character one Level: the next Level's progression Choices open on settling. Open Choices never block this. */
export async function advanceLevel(ctx: GuideContext, settle: Settle): Promise<GuideContext> {
  const next = ctx.character.level + 1;
  if (next > 20) return ctx;
  const character = ensureLevels({ ...ctx.character, level: next as LevelNumber }, next);
  return settle(character);
}

/** Set the Character's Level back (planned-ahead Choices above it stay in the log). */
export async function setLevel(ctx: GuideContext, level: LevelNumber, settle: Settle): Promise<GuideContext> {
  return settle(ensureLevels({ ...ctx.character, level }, level));
}
